/**
 * Sidebar navigation structure (data only — no JSX).
 * Drives <Sidebar> rendering + breadcrumb lookup.
 * RBAC gating: each leaf/link carries an `access` key; omitted or 'all' = every signed-in user,
 * 'admin' = admin only, others = admin OR the matching AccessProfile role flag. Groups have no
 * access of their own — a group hides once all of its children are filtered out.
 * This NAV array is the current IA. docs/menu-layout-design.md records the 2026-06-29 port plan
 * and has since diverged — read it for original intent, not for what the menu is now.
 * Route-level (non-sidebar) gating lives in nav-access.ts (canVisit), used by AppLayout's
 * redirect guard.
 */
import { GAME_OPEN_TO_ALL_EMPLOYEES, NAV_TOGGLE_LOCKED_PATHS, type AccessProfile } from '@system/shared'

import type { AccessKey } from '@system/shared'

export type NavLeaf = {
  labelKey: string
  to: string
  access?: AccessKey | AccessKey[]
  /** Path prefixes that also light this leaf up (leaves otherwise match their path exactly). */
  activeUnder?: string[]
}

export type NavItem =
  | { kind: 'link'; icon: string; labelKey: string; to: string; access?: AccessKey | AccessKey[] }
  | { kind: 'group'; icon: string; labelKey: string; id: string; children: NavLeaf[] }

/**
 * Whether a sidebar leaf is lit for the current route. Leaves match their own path exactly
 * (fuzzy would light "/marketing" up on "/marketing/competitor"); a leaf that owns whole
 * sections lists their prefixes in `activeUnder`, matched fuzzily.
 */
export function isLeafActive(leaf: NavLeaf, matches: (to: string, exact: boolean) => boolean): boolean {
  return matches(leaf.to, true) || (leaf.activeUnder ?? []).some((p) => matches(p, false))
}

// hasNavAccess + AccessKey live in @system/shared so the API runs the same predicate.
export { hasNavAccess, type AccessKey } from '@system/shared'
export const NAV: NavItem[] = [
  { kind: 'link', icon: 'home', labelKey: 'nav.dashboard', to: '/' },
  // Supernova Connect: trial players and the game admin during the trial, every internal employee once
  // it opens; the API decides who actually plays (exclusions) either way.
  { kind: 'link', icon: 'star', labelKey: 'nav.game', to: '/game', access: GAME_OPEN_TO_ALL_EMPLOYEES ? 'employee' : 'gamePlayer' },

  // Trackers — tabbed SPAs (tabs live inside the page, not the sidebar)
  {
    kind: 'group', icon: 'trend-up', labelKey: 'nav.marketing', id: 'marketing',
    children: [
      { labelKey: 'nav.marketing.reports', to: '/marketing', access: 'ua' },
      // The audience the report tab had: UA, monetization and developers all reach it.
      { labelKey: 'nav.marketing.appSpy', to: '/marketing/app-spy', access: ['ua', 'monet', 'dev'] },
      { labelKey: 'nav.marketing.playableAds', to: '/marketing/playable-ads', access: 'ua' },
      { labelKey: 'nav.marketing.localize', to: '/marketing/localize', access: 'localize' },
      { labelKey: 'nav.marketing.competitor', to: '/marketing/competitor', access: 'admin' },
      { labelKey: 'nav.marketing.creativeSpy', to: '/marketing/creative-spy', access: 'creativeSpy' },
      // Left the report's tabs on 2026-09-17: it is a working surface with its own layout, not a
      // view of the report, and it never used the report's date range or owner filter.
      { labelKey: 'nav.marketing.campaignEditor', to: '/marketing/campaign-editor', access: 'gadsEditor' },
      // The daily spend-vs-revenue check: UA owners for the spend rules, MO owners for match rate and waterfall.
      { labelKey: 'nav.marketing.uaAlerts', to: '/marketing/ua-alerts', access: ['ua', 'monet'] },
    ],
  },
  {
    kind: 'group', icon: 'money', labelKey: 'nav.monetization', id: 'monetization',
    children: [
      { labelKey: 'nav.monetization.reports', to: '/monetization', access: 'monet' },
      { labelKey: 'nav.monetization.ecpm', to: '/monetization/ecpm', access: 'monet' },
      { labelKey: 'nav.monetization.admob', to: '/monetization/admob-mediation', access: 'monet' },
      { labelKey: 'nav.monetization.encrypt', to: '/monetization/encrypt', access: 'monet' },
      { labelKey: 'nav.monetization.appAssignment', to: '/monetization/app-assignment', access: 'monetAssign' },
      { labelKey: 'nav.monetization.moLead', to: '/monetization/mo-lead', access: 'moLead' },
    ],
  },
  {
    kind: 'group', icon: 'code', labelKey: 'nav.developer', id: 'developer',
    children: [
      { labelKey: 'nav.developer.reports', to: '/developer', access: 'dev' },
      { labelKey: 'nav.developer.encrypt', to: '/developer/encrypt', access: 'devDept' },
      { labelKey: 'nav.developer.adsDecode', to: '/developer/ads-decode', access: 'admin' },
      // Issues + Docs (the Jira/Confluence replacement) open to every internal employee; which
      // projects a person sees is decided per project by membership (user decision 09/09/2026).
      // Docs is its own sidebar leaf directly under Issues (user decision 28/09/2026) — each lights
      // only under its own path; the in-section tabs (Spaces / My issues / Docs / Admin) still exist.
      { labelKey: 'nav.issues', to: '/issues', access: 'employee', activeUnder: ['/issues'] },
      { labelKey: 'nav.docs', to: '/docs', access: 'employee', activeUnder: ['/docs'] },
    ],
  },

  {
    kind: 'group', icon: 'rocket', labelKey: 'nav.growth', id: 'growth',
    children: [
      { labelKey: 'nav.growth.uaBotConsole', to: '/growth/ua-bot-console', access: 'admin' },
      { labelKey: 'nav.growth.asoTranslate', to: '/growth/aso-translate', access: 'aso' },
      { labelKey: 'nav.growth.firebaseRc', to: '/growth/firebase-rc', access: 'monet' },
      { labelKey: 'nav.growth.eventAnalytics', to: '/growth/event-analytics', access: 'monet' },
      { labelKey: 'nav.growth.siteContent', to: '/growth/site-content', access: ['cmo', 'hr'] },
    ],
  },
  {
    // HR Admin — HR LEAD only (HR staff manage just Public Site Content under Growth).
    kind: 'group', icon: 'briefcase', labelKey: 'nav.hrAdmin', id: 'hrAdmin',
    children: [
      { labelKey: 'nav.employees', to: '/employees', access: 'hrLead' },
      { labelKey: 'nav.hrAdmin.leave', to: '/hr/leave', access: 'hrLead' },
      { labelKey: 'nav.hrAdmin.config', to: '/hr/config', access: 'hrLead' },
      { labelKey: 'nav.hrAdmin.loginLogs', to: '/hr/login-logs', access: 'hrLead' },
    ],
  },
  {
    // Admin — Products plus the settings that used to be tabs of it. API Tokens lived inside
    // Products to spend one fewer top-level entry on something only admins touch; that held for
    // one such panel, but at two Products carried five tabs of which two were not product data.
    // A group keeps the single entry and has somewhere to put the next setting.
    //
    // One 'Settings' leaf rather than a leaf per panel: leaves render through <Link to>, and the
    // router serialises search as JSON, so to: '/admin?tab=tokens' would arrive as ?tab="tokens"
    // and land on the default tab (the same trap connector-tokens-redirect documents). The panels
    // are therefore tabs inside /admin, not siblings here.
    kind: 'group', icon: 'gear', labelKey: 'nav.admin', id: 'admin',
    children: [
      // First child: a collapsed group navigates here on click (sidebar.tsx), and Products is
      // the one of the two that gets daily use.
      { labelKey: 'nav.products', to: '/products', access: 'admin' },
      // SPN004 (canToggleNav) also reaches this leaf — the /admin page hosts the Sidebar tab,
      // its one reason to be there; the other tabs stay admin-only and 403 at their own API.
      { labelKey: 'nav.admin.settings', to: '/admin', access: ['admin', 'toggleNav'] },
      // The Play report collector machines: SPN004 holds their keys, the CTO may download the payment receipts
      // (user decision 28/10/2026). canOpenPage and the API's own check are the real gate.
      { labelKey: 'nav.admin.storeMachines', to: '/admin/store-machines', access: ['toggleNav', 'cto'] },
      // OKR dashboard admin — the roles that enter Objective/KR targets and check-ins (admin, CTO,
      // CMO, HR Lead, Design Lead, Monet Lead), unlike its admin-only siblings above; nav-access.ts
      // (canVisit) and the API's own RolesGuard are the real gate. Moved here from the sidebar
      // footer (user request 2026-09-24) — a group survives with only this leaf visible for a
      // non-admin team-editor caller, same mixed-role pattern hasNavAccess already gives every group.
      { labelKey: 'nav.okrAdmin', to: '/okr/admin', access: ['cto', 'cmo', 'hrLead', 'designLead', 'monetLead'] },
    ],
  },

  {
    kind: 'group', icon: 'wallet', labelKey: 'nav.finance', id: 'finance',
    children: [
      // 'Revenue & Cost' (/finance) + 'Products' (/finance/products) sub-items removed per request.
      { labelKey: 'nav.finance.commission', to: '/finance/commission', access: ['admin', 'commission'] },
      { labelKey: 'nav.finance.dividend', to: '/finance/dividend-distribution', access: 'admin' },
    ],
  },

  {
    // Quiz visibility is finished in sidebar.tsx from /api/quiz/my-access (QuizHost role
    // is not part of AccessProfile): non-hosts keep ONLY the play leaf and only on
    // MOBILE (user 260721); hosts/admins see all three on every device.
    kind: 'group', icon: 'play', labelKey: 'nav.quiz', id: 'quiz',
    children: [
      { labelKey: 'nav.quiz.play', to: '/quiz', access: 'employee' },
      // History + management live as tabs inside /quiz/host (user 260721).
      { labelKey: 'nav.quiz.host', to: '/quiz/host', access: 'employee' },
    ],
  },

  // 'Bài thi của tôi' (/exam/my) lives in the sidebar FOOTER menu (sidebar.tsx footerLinks), not here.
  {
    kind: 'group', icon: 'clipboard', labelKey: 'nav.examAdmin', id: 'examAdmin',
    children: [
      { labelKey: 'nav.examAdmin.tests', to: '/exam/admin/tests', access: 'admin' },
      { labelKey: 'nav.examAdmin.questions', to: '/exam/admin/questions', access: 'admin' },
      { labelKey: 'nav.examAdmin.create', to: '/exam/admin/create', access: 'admin' },
    ],
  },
]

/** Flat map { path -> labelKey } for breadcrumb / title lookup. */
export const PATH_LABEL: Record<string, string> = NAV.reduce((acc, item) => {
  if (item.kind === 'link') acc[item.to] = item.labelKey
  else item.children.forEach((c) => { acc[c.to] = c.labelKey })
  return acc
}, {} as Record<string, string>)

/**
 * Title key for a pathname, including the dynamic routes PATH_LABEL cannot list
 * (/issues/NP27, /docs/some-page). Exact matches win; prefixes fall back to their section.
 * Looked up in the sidebar NAV, then the sidebar footer menu, then EXTRA_TITLES. A route found in
 * none of them gets the app name as its header title, so page-titles.spec.ts keeps every route here.
 */
export function titleKeyForPath(pathname: string): string | undefined {
  const exact = PATH_LABEL[pathname] ?? FOOTER_LINKS.find((l) => l.to === pathname)?.labelKey ?? EXTRA_TITLES[pathname]
  if (exact) return exact
  if (pathname.startsWith('/issues/')) return 'nav.issues'
  if (pathname.startsWith('/docs/')) return 'nav.docs'
  return undefined
}

/** Titles for routes that are in neither the sidebar NAV nor the footer menu. */
export const EXTRA_TITLES: Record<string, string> = {
  // Issues tabs — no sidebar leaf of their own since 09/09/2026, still need a header title.
  '/issues/my': 'nav.myIssues',
  '/issues/admin': 'nav.issuesAdmin',
  '/issues/report': 'issues.report.title',
  // Reached from a button, a notification or another page, never from the menu.
  '/handbook/start': 'nav.handbook',
  '/licenses': 'lic.title',
  '/team-vote': 'teamvote.title',
  '/game/admin': 'nav.game',
  '/game/handout': 'nav.game',
  '/exam/take': 'nav.examTake',
  '/exam/result': 'nav.examResult',
  '/exam/admin/grade': 'nav.examAdmin.grade',
  '/exam/admin/results': 'nav.examAdmin.results',
}

/**
 * Sidebar footer links (sidebar.tsx footer menu), outside the NAV tree above since they render
 * from their own list rather than NAV groups. Kept here, one array, so the sidebar toggle admin
 * tab and the Sidebar footer render the exact same set under the exact same labels.
 */
export const FOOTER_LINKS: { to: string; labelKey: string }[] = [
  { to: '/profile', labelKey: 'nav.profile' },
  { to: '/leave', labelKey: 'nav.leave' },
  { to: '/exam/my', labelKey: 'nav.examMy' },
  { to: '/handbook', labelKey: 'nav.handbook' },
  { to: '/travel-expense', labelKey: 'nav.travelExpense' },
  // Open to everyone signed in, unlike the four above — sidebar.tsx renders it unconditionally
  // (no employee/restricted gating); only its disabled/badge state comes from this list.
  { to: '/help', labelKey: 'nav.help' },
]

/**
 * Every path pattern `disabledNavFor` matches against, mapped back to the NAV entry it belongs to.
 * A leaf's `activeUnder` prefixes resolve to that SAME leaf's `to` — Issues and Docs are now
 * separate leaves (`activeUnder: ['/issues']` and `['/docs']` respectively, user decision
 * 28/09/2026), so a `/docs/*` page is switched off with the Docs leaf alone, independently of
 * `/issues` (mirrors `isLeafActive`, which treats `activeUnder` as part of the same leaf for
 * lighting up the sidebar).
 */
function allNavPathCandidates(): { match: string; to: string }[] {
  const candidates: { match: string; to: string }[] = []
  for (const item of NAV) {
    if (item.kind === 'link') {
      candidates.push({ match: item.to, to: item.to })
    } else {
      for (const c of item.children) {
        candidates.push({ match: c.to, to: c.to })
        for (const prefix of c.activeUnder ?? []) candidates.push({ match: prefix, to: c.to })
      }
    }
  }
  for (const l of FOOTER_LINKS) candidates.push({ match: l.to, to: l.to })
  return candidates
}

export type ToggleableNavEntry = { labelKey: string; to: string; access?: AccessKey | AccessKey[] }
export type ToggleableNavGroup = { groupLabelKey: string; entries: ToggleableNavEntry[] }

/** Footer links carry no `access` of their own: '/help' is for everyone signed in, the rest for employees. */
function footerAccess(to: string): AccessKey {
  return to === '/help' ? 'all' : 'employee'
}

/**
 * Every switchable sidebar entry for the admin tab, grouped the way the sidebar itself groups
 * them: a top-level link is its own one-entry group, a NAV group's leaves share its labelKey, and
 * the footer links form one more group. Locked paths (NAV_TOGGLE_LOCKED_PATHS) are left out —
 * there is nothing to switch off for the dashboard or the page that hosts this very switch.
 */
export function toggleableNavGroups(): ToggleableNavGroup[] {
  const groups: ToggleableNavGroup[] = []
  for (const item of NAV) {
    if (item.kind === 'link') {
      if (NAV_TOGGLE_LOCKED_PATHS.includes(item.to)) continue
      groups.push({ groupLabelKey: item.labelKey, entries: [{ labelKey: item.labelKey, to: item.to, access: item.access }] })
    } else {
      const entries = item.children
        .filter((c) => !NAV_TOGGLE_LOCKED_PATHS.includes(c.to))
        .map((c) => ({ labelKey: c.labelKey, to: c.to, access: c.access }))
      if (entries.length > 0) groups.push({ groupLabelKey: item.labelKey, entries })
    }
  }
  groups.push({
    groupLabelKey: 'navToggles.footerGroup',
    entries: FOOTER_LINKS
      .filter((l) => !NAV_TOGGLE_LOCKED_PATHS.includes(l.to))
      .map((l) => ({ ...l, access: footerAccess(l.to) })),
  })
  return groups
}

/**
 * The `to` of the single most specific NAV/footer entry lit up for `pathname`: an exact match
 * (against a leaf's own `to` OR one of its `activeUnder` prefixes) wins over any longer-prefix
 * search, and among prefixes the longest one (with a '/' boundary, so '/marketing' does not
 * swallow '/marketing/competitor') wins. Mirrors isLeafActive's own "own path first" rule.
 */
export function mostSpecificNavPath(pathname: string): string | undefined {
  const candidates = allNavPathCandidates()
  const exact = candidates.find((c) => c.match === pathname)
  if (exact) return exact.to
  let best: { match: string; to: string } | undefined
  for (const c of candidates) {
    if (pathname.startsWith(`${c.match}/`)) {
      if (!best || c.match.length > best.match.length) best = c
    }
  }
  return best?.to
}

/**
 * True when this page is switched off for the caller: SPN004 (`canToggleNav`) always sees
 * through — they need to be able to open a disabled page to test it before turning it back on.
 * Everyone else, including app-wide admins, is blocked the moment the most specific NAV/footer
 * entry for the path is on the disabled list.
 */
export function disabledNavFor(pathname: string, access: AccessProfile): boolean {
  if (access.canToggleNav) return false
  const match = mostSpecificNavPath(pathname)
  return !!match && access.disabledNav.includes(match)
}
